import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText, formatDate } from '../api';
import { useAuth } from '../auth';
import type { BenutzerKurz, Einsatz, EinsatzKurz } from '../types';
import { Badge, Card, Dialog, Field, Notice, kundenName } from './ui';

const zeit = (t: string | null) => (t ? t.slice(0, 5) : '');
export const zeitfenster = (von: string | null, bis: string | null) => (von ? `${zeit(von)}${bis ? `–${zeit(bis)}` : ''} Uhr` : 'ganztägig');

export const heute = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
export const plusTage = (datum: string, tage: number) => {
  const d = new Date(`${datum}T12:00:00`);
  d.setDate(d.getDate() + tage);
  return d.toLocaleDateString('sv-SE');
};

/** Adresse als Google-Maps-Link - für Navigation vom Handy aus. */
export const kartenLink = (strasse: string | null, plz: string | null, ort: string | null) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([strasse, plz, ort].filter(Boolean).join(', '))}`;

/**
 * Termin planen oder verlegen. Beim Verlegen ist ein Grund Pflicht - er steht
 * danach im Verlauf des Vorgangs.
 */
export function EinsatzPlanenDialog({
  ziel,
  einsatz,
  onClose,
  onFertig,
}: {
  ziel?: { lieferungId?: string; serviceauftragId?: string };
  einsatz?: { id: string; datum: string; zeit_von: string | null; zeit_bis: string | null; mitarbeiterIds: string[] };
  onClose: () => void;
  onFertig: () => void;
}) {
  const { user } = useAuth();
  const [datum, setDatum] = useState(einsatz?.datum ?? plusTage(heute(), 1));
  const [von, setVon] = useState(zeit(einsatz?.zeit_von ?? '09:00'));
  const [bis, setBis] = useState(zeit(einsatz?.zeit_bis ?? '12:00'));
  const [mitarbeiter, setMitarbeiter] = useState<string[]>(einsatz?.mitarbeiterIds ?? []);
  const [hinweis, setHinweis] = useState('');
  const [grund, setGrund] = useState('');
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [fehler, setFehler] = useState('');
  const [busy, setBusy] = useState(false);
  const verlegen = !!einsatz;

  useEffect(() => {
    void api<BenutzerKurz[]>('/api/benutzer/auswahl').then((b) => {
      setBenutzer(b);
      // Vorschlag: beim Außendienst der Techniker selbst, sonst niemand - lieber bewusst wählen.
      if (!einsatz && ziel?.serviceauftragId && user?.rolle === 'techniker') setMitarbeiter([user.id]);
    });
  }, [einsatz, ziel?.serviceauftragId, user]);

  const umschalten = (id: string) => setMitarbeiter((alt) => (alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id]));

  async function speichern() {
    setBusy(true);
    setFehler('');
    try {
      const body = { datum, zeitVon: von || null, zeitBis: bis || null, mitarbeiterIds: mitarbeiter, hinweis: hinweis || null };
      if (verlegen) await api(`/api/einsaetze/${einsatz!.id}/umplanen`, { method: 'POST', body: { ...body, grund } });
      else await api('/api/einsaetze', { method: 'POST', body: { ...body, ...ziel } });
      onFertig();
    } catch (e) {
      setFehler(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  // Wer fährt raus? Fahrer und Techniker zuerst, der Rest danach.
  const sortiert = [...benutzer].sort((a, b) => Number(a.rolle === 'admin' || a.rolle === 'verkauf') - Number(b.rolle === 'admin' || b.rolle === 'verkauf'));

  return (
    <Dialog
      titel={verlegen ? 'Termin verlegen' : 'Termin planen'}
      onClose={onClose}
      aktionen={
        <>
          <button onClick={onClose}>Abbrechen</button>
          <button className="primary" disabled={busy || !datum || mitarbeiter.length === 0 || (verlegen && grund.trim().length < 3)} onClick={() => void speichern()}>
            {verlegen ? 'Verlegen' : 'Termin festlegen'}
          </button>
        </>
      }
    >
      {fehler && <Notice kind="error">{fehler}</Notice>}
      <div className="grid3">
        <Field label="Datum">
          <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </Field>
        <Field label="von">
          <input type="time" value={von} onChange={(e) => setVon(e.target.value)} />
        </Field>
        <Field label="bis">
          <input type="time" value={bis} onChange={(e) => setBis(e.target.value)} />
        </Field>
      </div>
      <div className="field">
        <span>Wer fährt raus? (mindestens einer)</span>
        <div className="row">
          {sortiert.map((b) => (
            <label key={b.id} className={mitarbeiter.includes(b.id) ? 'badge info' : 'badge'} style={{ cursor: 'pointer', padding: '4px 10px' }}>
              <input type="checkbox" style={{ width: 'auto', marginRight: 6 }} checked={mitarbeiter.includes(b.id)} onChange={() => umschalten(b.id)} />
              {b.name}
            </label>
          ))}
        </div>
      </div>
      <Field label="Hinweis für die Fahrt (optional)">
        <input value={hinweis} onChange={(e) => setHinweis(e.target.value)} placeholder="z. B. vorher anrufen, 3. OG ohne Aufzug" />
      </Field>
      {verlegen && (
        <Field label="Grund der Verlegung (Pflicht)">
          <input value={grund} onChange={(e) => setGrund(e.target.value)} />
        </Field>
      )}
      <p className="small muted" style={{ margin: 0 }}>
        Der Termin wird zur Wiedervorlage: Liegt am Abend kein Protokoll vor, ist der Vorgang überfällig.
      </p>
    </Dialog>
  );
}

/** Termin-Kasten auf den Detailseiten von Lieferung und Außendienst. */
export function EinsatzPanel({
  einsaetze,
  planbar,
  ziel,
  onNeu,
}: {
  einsaetze: EinsatzKurz[];
  planbar: boolean;
  ziel: { lieferungId?: string; serviceauftragId?: string };
  onNeu: () => void;
}) {
  const { can, user } = useAuth();
  const [planen, setPlanen] = useState(false);
  const [verlegen, setVerlegen] = useState<EinsatzKurz | null>(null);
  const [ids, setIds] = useState<string[]>([]);
  const geplant = einsaetze.find((e) => e.status === 'geplant');
  const darfPlanen = can('admin', 'verkauf') || (!!ziel.serviceauftragId && user?.rolle === 'techniker');

  async function verlegenOeffnen(e: EinsatzKurz) {
    const voll = await api<Einsatz>(`/api/einsaetze/${e.id}`);
    setIds(voll.mitarbeiter.map((m) => m.id));
    setVerlegen(e);
  }

  return (
    <Card title="Termin beim Kunden">
      <div className="stack" style={{ gap: 10 }}>
        {geplant ? (
          <div className="dublette">
            <div className="wer">
              <strong>
                {formatDate(geplant.datum)}, {zeitfenster(geplant.zeit_von, geplant.zeit_bis)}
              </strong>
              <div className="small muted">
                <span className="mono">{geplant.nummer}</span> · {geplant.mitarbeiter}
              </div>
            </div>
            <Link className="button primary klein" to={`/einsaetze/${geplant.id}`}>
              Protokoll / Details
            </Link>
            {darfPlanen && (
              <button className="klein" onClick={() => void verlegenOeffnen(geplant)}>
                Verlegen
              </button>
            )}
          </div>
        ) : planbar ? (
          <div className="row">
            <span className="muted">Noch kein Termin.</span>
            {darfPlanen && (
              <button className="primary" onClick={() => setPlanen(true)}>
                Termin planen
              </button>
            )}
          </div>
        ) : (
          <span className="muted small">Kein offener Termin.</span>
        )}
        {einsaetze.filter((e) => e.status !== 'geplant').length > 0 && (
          <table>
            <tbody>
              {einsaetze
                .filter((e) => e.status !== 'geplant')
                .map((e) => (
                  <tr key={e.id}>
                    <td className="mono small">
                      <Link to={`/einsaetze/${e.id}`}>{e.nummer}</Link>
                    </td>
                    <td className="small">{formatDate(e.datum)}</td>
                    <td className="small muted">{e.mitarbeiter}</td>
                    <td style={{ textAlign: 'right' }}>
                      {e.status === 'erledigt' ? <Badge kind="ok">Protokoll</Badge> : <Badge kind="warn">abgebrochen: {e.abbruch_grund}</Badge>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
      {planen && (
        <EinsatzPlanenDialog
          ziel={ziel}
          onClose={() => setPlanen(false)}
          onFertig={() => {
            setPlanen(false);
            onNeu();
          }}
        />
      )}
      {verlegen && (
        <EinsatzPlanenDialog
          einsatz={{ id: verlegen.id, datum: verlegen.datum, zeit_von: verlegen.zeit_von, zeit_bis: verlegen.zeit_bis, mitarbeiterIds: ids }}
          onClose={() => setVerlegen(null)}
          onFertig={() => {
            setVerlegen(null);
            onNeu();
          }}
        />
      )}
    </Card>
  );
}

/** Eine Zeile für Tageslisten (Mein Tag, Tagesroute). */
export function EinsatzZeile({
  e,
}: {
  e: { id: string; nummer: string; art: string; zeit_von: string | null; zeit_bis: string | null; strasse: string | null; plz: string | null; ort: string | null; status: string; titel?: string | null; artikel?: string | null; geraet?: string | null; kunde_vorname: string | null; kunde_nachname: string | null; kunde_firma: string | null };
}) {
  const titel = e.titel ?? e.artikel ?? e.geraet ?? '';
  return (
    <Link to={`/einsaetze/${e.id}`} className={e.status === 'erledigt' ? 'ticket' : 'ticket heute'}>
      <span className="typ">{e.art === 'lieferung' ? '🚚' : '🔧'}</span>
      <div>
        <div className="titel">{kundenName({ vorname: e.kunde_vorname, nachname: e.kunde_nachname, firma: e.kunde_firma })}</div>
        <div className="meta">
          <span>{[e.strasse, [e.plz, e.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>
          <span>{titel}</span>
        </div>
      </div>
      <div className="frist">
        {zeitfenster(e.zeit_von, e.zeit_bis)}
        <div>{e.status === 'erledigt' ? <Badge kind="ok">erledigt</Badge> : <span className="mono small">{e.nummer}</span>}</div>
      </div>
    </Link>
  );
}

/**
 * Unterschriftenfeld. Zeichnet mit Maus, Finger oder Stift und liefert ein
 * PNG als Data-URL. Leer gilt als nicht unterschrieben.
 */
export function Unterschrift({ onChange }: { onChange: (png: string | null) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const zeichnet = useRef(false);
  const leer = useRef(true);

  useEffect(() => {
    const c = canvas.current!;
    const skala = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * skala;
    c.height = c.offsetHeight * skala;
    const ctx = c.getContext('2d')!;
    ctx.scale(skala, skala);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0b1f4d';
  }, []);

  const punkt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  return (
    <div className="stack" style={{ gap: 6 }}>
      <canvas
        ref={canvas}
        className="unterschrift"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          zeichnet.current = true;
          const ctx = canvas.current!.getContext('2d')!;
          const p = punkt(e);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
        }}
        onPointerMove={(e) => {
          if (!zeichnet.current) return;
          const ctx = canvas.current!.getContext('2d')!;
          const p = punkt(e);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
          leer.current = false;
        }}
        onPointerUp={() => {
          zeichnet.current = false;
          onChange(leer.current ? null : canvas.current!.toDataURL('image/png'));
        }}
      />
      <div>
        <button
          type="button"
          className="link small"
          onClick={() => {
            const c = canvas.current!;
            c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
            leer.current = true;
            onChange(null);
          }}
        >
          Unterschrift löschen
        </button>
      </div>
    </div>
  );
}
